import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * Every delete, one way (spec 129 FR-005).
 *
 * It names the item and what deleting it does, labels the action with its
 * verb, cannot be dismissed while the request is in flight, and — the part
 * pages used to differ on — stays open when the service refuses, with the
 * service's reason inside it. Success is the page's to say (a toast), because
 * the page knows the item's name and the dialog is gone by then.
 */
export function ConfirmDestructiveDialog({
  open,
  title,
  children,
  confirmLabel,
  isPending,
  error,
  confirmDisabled = false,
  pendingLabel = 'Deleting…',
  confirmVariant = 'destructive',
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: ReactNode;
  /** What deleting it does. */
  children: ReactNode;
  /** The verb and object: "Delete connection". */
  confirmLabel: string;
  isPending: boolean;
  /** The service's reason for refusing, shown inline. */
  error?: string | null;
  /**
   * Holds the action back for a reason the description already gives — the
   * default image, a folder whose contents are still being counted.
   */
  confirmDisabled?: boolean;
  /** What the action reads while in flight, for a verb other than delete. */
  pendingLabel?: string;
  /**
   * A consequential change that destroys nothing (requiring an account, spec
   * 160) asks the same way, without the destructive colour.
   */
  confirmVariant?: 'destructive' | 'default';
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (isPending) return;
        if (!next) onCancel();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{children}</DialogDescription>
        </DialogHeader>

        {error ? (
          <p role="alert" className="text-destructive text-sm">
            {error}
          </p>
        ) : null}

        <DialogFooter>
          <DialogClose
            render={<Button type="button" variant="outline" />}
            disabled={isPending}
          >
            Cancel
          </DialogClose>
          <Button
            variant={confirmVariant}
            onClick={onConfirm}
            disabled={isPending || confirmDisabled}
          >
            {isPending ? pendingLabel : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
